import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useEffect } from "react";

interface DraggableModalProps {
  /** Header */
  header?: ReactNode;
  /** Body */
  children: ReactNode;
  /** Footer */
  footer?: ReactNode;
  /** Open/hide */
  open?: boolean;
  /** On close callback */
  onClose?: () => void;
  /** Specify the size */
  size?: string;
  /** Modal class name */
  modalClassName?: string;
  /** Header class name */
  headerClassName?: string;
  /** Body class name */
  bodyClassName?: string;
  /** Footer class name */
  footerClassName?: string;
  /** Animation duration in seconds */
  animationDuration?: number;
  /** If false, modal cannot be closed (disables drag and backdrop click) */
  canClose?: boolean;
}

/**
 * A draggable modal that can be closed by dragging down
 */
export const DraggableModal = ({
  header,
  children,
  footer,
  open,
  onClose,
  size,
  modalClassName = "rounded-t-lg",
  headerClassName,
  bodyClassName,
  footerClassName,
  animationDuration = 0.3,
  canClose = true,
}: DraggableModalProps) => {
  // Add overflow-hidden to the body when the modal is open
  useEffect(() => {
    if (open) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [open]);

  const handleClose = () => {
    if (canClose) {
      onClose?.();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-transparent"
            onClick={canClose ? handleClose : undefined}
            data-testid="div-dismiss-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: animationDuration * 0.67,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className={cx(
              "fixed bottom-0 left-0 right-0 z-50 mx-auto flex max-h-[calc(100%_-_env(safe-area-inset-top)_-_12px)] w-full flex-col bg-white sm:w-[390px]",
              size,
              modalClassName
            )}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: animationDuration, ease: "easeInOut" }}
            drag={canClose ? "y" : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={
              canClose
                ? (_, info) => {
                    // Close modal if dragged down more than 100px or with sufficient velocity
                    if (info.offset.y > 100 || info.velocity.y > 500) {
                      handleClose();
                    }
                  }
                : undefined
            }
          >
            {/* Drag indicator - only show if modal can be closed */}
            {canClose && (
              <div className="flex w-full justify-center py-2">
                <div className="h-1 w-10 rounded-full bg-gray-300" />
              </div>
            )}

            <div className={cx("flex-none", headerClassName)}>{header}</div>
            <div className={cx("grow overflow-auto", bodyClassName)}>
              {children}
            </div>
            <div
              className={cx(
                "flex-none pb-[env(safe-area-inset-bottom)]",
                footerClassName
              )}
            >
              {footer}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
